import React, { useState } from "react";
import {
    CheckCircle2,
    Clock,
    ScrollText,
    Building2,
    Calendar,
    Coins,
    Scale,
    AlertTriangle,
    Sparkles,
    Gavel,
    Target,
    Tag,
    Layers,
    ShieldCheck,
    ChevronDown,
    FileText,
    TrendingUp,
    Award,
    Send,
    Wallet,
    Users,
    MapPin,
    Banknote,
    BadgePercent,
    HardHat,
    Shield,
    Truck,
    Brush,
    Wrench,
    Package,
    Briefcase,
} from "lucide-react";

// Icon mapping for each summary section by its title prefix
const SECTION_ICONS = {
    "Objeto": Target,
    "Importe": Coins,
    "Duración": Clock,
    "Procedimiento": Gavel,
    "Criterios": Award,
    "Solvencia": ShieldCheck,
    "Garantías": Scale,
    "Presentación": Send,
    "Riesgos": AlertTriangle,
    "Oportunidades": TrendingUp,
    "Personal": Users,
    "Presupuesto": HardHat,
};

const iconForSection = (title = "") => {
    for (const k of Object.keys(SECTION_ICONS)) {
        if (title.startsWith(k)) return SECTION_ICONS[k];
    }
    return FileText;
};

const SummarySection = ({ section, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    const Icon = iconForSection(section.title || "");
    return (
        <div
            className="border border-slate-200 rounded-2xl bg-white overflow-hidden"
            data-testid={`summary-section-${(section.title || "").slice(0, 20)}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors text-left"
            >
                <div className="w-8 h-8 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                    <Icon size={15} />
                </div>
                <div className="flex-1 font-display font-bold text-ink-950 text-base">
                    {section.title || "Sección"}
                </div>
                <ChevronDown
                    size={16}
                    className={`text-ink-400 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div className="px-4 pb-5 pt-1">
                    <div className="pl-11 text-sm text-ink-700 leading-relaxed whitespace-pre-line">
                        {section.content || "—"}
                    </div>
                </div>
            )}
        </div>
    );
};

const Card = ({ title, icon: Icon, children, span = "col-span-1" }) => (
    <div className={`bg-white border border-slate-200 p-5 ${span}`}>
        <div className="flex items-center gap-2 overline mb-3">
            {Icon && <Icon size={12} />} {title}
        </div>
        {children}
    </div>
);

const List = ({ items, empty = "Sin datos", mono = false }) => {
    if (!items?.length) return <div className="text-sm text-slate-400">{empty}</div>;
    return (
        <ul className="space-y-2">
            {items.map((it, i) => (
                <li
                    key={`${i}-${it}`}
                    className={`flex items-start gap-2 text-sm text-slate-700 leading-relaxed ${
                        mono ? "font-mono text-xs" : ""
                    }`}
                >
                    <CheckCircle2 size={13} className="text-[#0055ff] mt-1 shrink-0" />
                    <span>{it}</span>
                </li>
            ))}
        </ul>
    );
};

export const AnalysisView = ({ analysis }) => {
    if (!analysis) return null;

    const Highlight = ({ label, value, icon: Icon }) => (
        <div className="flex gap-3">
            <div className="w-8 h-8 bg-[#eff5ff] text-[#0055ff] flex items-center justify-center rounded-xl border border-sky-100 shrink-0">
                <Icon size={14} />
            </div>
            <div className="min-w-0">
                <div className="overline mb-0.5">{label}</div>
                <div className="text-sm text-slate-900 font-medium break-words">{value || "—"}</div>
            </div>
        </div>
    );

    return (
        <div className="space-y-5" data-testid="analysis-view">
            {/* Executive summary intro */}
            <div className="bg-[#0055ff] text-white p-6 md:p-8 rounded-xl fade-up">
                <div className="flex items-center gap-2 overline !text-sky-300 mb-3">
                    <Sparkles size={12} />
                    Resumen ejecutivo IA
                </div>
                <p className="text-base md:text-lg leading-relaxed font-display font-medium max-w-4xl whitespace-pre-line">
                    {analysis.executive_summary || "Sin resumen generado."}
                </p>
            </div>

            {/* Structured summary sections (collapsible) */}
            {analysis.summary_sections?.length > 0 && (
                <div className="space-y-3" data-testid="summary-sections">
                    <div className="flex items-center justify-between">
                        <h3 className="font-display text-lg font-black tracking-tight">
                            Análisis detallado por bloques
                        </h3>
                        <div className="text-xs text-ink-500 font-medium">
                            {analysis.summary_sections.length} secciones · pulsa para expandir
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        {analysis.summary_sections.map((s, i) => (
                            <SummarySection
                                key={`${i}-${s.title}`}
                                section={s}
                                defaultOpen={i < 2}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Key highlights */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200 bg-white">
                <div className="p-5 border-r border-slate-200">
                    <Highlight label="Objeto" value={analysis.object} icon={Target} />
                </div>
                <div className="p-5 md:border-r border-slate-200 border-t md:border-t-0">
                    <Highlight label="Procedimiento" value={analysis.procedure} icon={Gavel} />
                </div>
                <div className="p-5 lg:border-r border-slate-200 border-t lg:border-t-0">
                    <Highlight label="Presupuesto" value={analysis.estimated_budget} icon={Coins} />
                </div>
                <div className="p-5 border-t lg:border-t-0">
                    <Highlight label="Duración" value={analysis.duration} icon={Clock} />
                </div>
            </div>

            {/* CPV + Guarantees row */}
            {(analysis.cpv?.length > 0 ||
                analysis.provisional_guarantee ||
                analysis.definitive_guarantee) && (
                <div className="grid md:grid-cols-3 gap-0 border border-slate-200 bg-white">
                    <div className="p-5 md:border-r border-slate-200">
                        <div className="flex items-center gap-2 overline mb-3">
                            <Tag size={12} /> Códigos CPV
                        </div>
                        {analysis.cpv?.length ? (
                            <div className="flex flex-wrap gap-1.5">
                                {analysis.cpv.map((code, i) => (
                                    <span
                                        key={`cpv-${code}-${i}`}
                                        className="font-mono text-xs bg-[#0055ff] text-white px-2 py-1 rounded-xl"
                                        data-testid={`cpv-code-${i}`}
                                    >
                                        {code}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-slate-400">Sin CPV identificado</div>
                        )}
                    </div>
                    <div className="p-5 md:border-r border-slate-200 border-t md:border-t-0">
                        <div className="flex items-center gap-2 overline mb-3">
                            <ShieldCheck size={12} /> Garantía provisional
                        </div>
                        <div className="font-mono text-sm font-medium text-slate-900">
                            {analysis.provisional_guarantee || "—"}
                        </div>
                    </div>
                    <div className="p-5 border-t md:border-t-0">
                        <div className="flex items-center gap-2 overline mb-3">
                            <ShieldCheck size={12} /> Garantía definitiva
                        </div>
                        <div className="font-mono text-sm font-medium text-slate-900">
                            {analysis.definitive_guarantee || "—"}
                        </div>
                    </div>
                </div>
            )}

            {/* Lots table */}
            {analysis.lots?.length > 0 && (
                <div className="bg-white border border-slate-200" data-testid="lots-table">
                    <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                        <Layers size={14} className="text-[#0055ff]" />
                        <div className="font-display font-medium">
                            División por lotes ({analysis.lots.length})
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full data-table">
                            <thead>
                                <tr>
                                    <th className="w-16">Lote</th>
                                    <th>Nombre</th>
                                    <th>Objeto</th>
                                    <th className="w-40">Presupuesto</th>
                                    <th className="w-36">CPV</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis.lots.map((l, i) => (
                                    <tr key={`lot-${i}-${l.number || l.name}`} data-testid={`lot-row-${i}`}>
                                        <td className="num font-semibold text-[#0055ff]">
                                            {l.number || i + 1}
                                        </td>
                                        <td className="font-medium">{l.name || "—"}</td>
                                        <td className="text-slate-600 text-sm">
                                            {l.object || "—"}
                                        </td>
                                        <td className="num">{l.budget || "—"}</td>
                                        <td>
                                            {l.cpv ? (
                                                <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-xl">
                                                    {l.cpv}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Subrogated personnel table (SAGRADO en servicios) */}
            {analysis.subrogated_personnel?.length > 0 && (
                <div
                    className="bg-white border border-amber-200 rounded-xl overflow-hidden"
                    data-testid="subrogated-personnel-table"
                >
                    <div className="px-5 py-3 border-b border-amber-200 bg-amber-50/60 flex items-center gap-2">
                        <Users size={14} className="text-amber-700" />
                        <div className="font-display font-bold text-ink-950">
                            Personal a subrogar ({analysis.subrogated_personnel.length})
                        </div>
                        <span className="text-xs text-amber-700 font-bold uppercase tracking-wider ml-auto">
                            ⚠️ Coste mínimo obligatorio
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full data-table text-sm">
                            <thead>
                                <tr>
                                    <th>Categoría</th>
                                    <th className="w-28">Antigüedad</th>
                                    <th className="w-24">Jornada</th>
                                    <th className="w-36">Salario bruto/año</th>
                                    <th>Convenio</th>
                                    <th>Observaciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis.subrogated_personnel.map((p, i) => (
                                    <tr
                                        key={`sp-${i}`}
                                        data-testid={`subrog-row-${i}`}
                                    >
                                        <td className="font-medium">{p.categoria || "—"}</td>
                                        <td className="text-slate-600">
                                            {p.antiguedad || "—"}
                                        </td>
                                        <td className="text-slate-600">
                                            {p.jornada || "—"}
                                        </td>
                                        <td className="num font-mono text-xs text-emerald-700 font-bold">
                                            {p.salario_bruto_anual || "—"}
                                        </td>
                                        <td className="text-slate-600 text-xs">
                                            {p.convenio || "—"}
                                        </td>
                                        <td className="text-slate-500 text-xs">
                                            {p.observaciones || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Operational details — per contract-type rich block */}
            <OperationalDetailsBlock details={analysis.operational_details} />

            {/* Bill of quantities table (SAGRADO en obras / suministros / consultoría) */}
            {analysis.bill_of_quantities?.length > 0 && (
                <div
                    className="bg-white border border-indigo-200 rounded-xl overflow-hidden"
                    data-testid="bill-of-quantities-table"
                >
                    <div className="px-5 py-3 border-b border-indigo-200 bg-indigo-50/60 flex items-center gap-2">
                        <HardHat size={14} className="text-indigo-700" />
                        <div className="font-display font-bold text-ink-950">
                            Presupuesto detallado por partidas ({analysis.bill_of_quantities.length})
                        </div>
                        <span className="text-xs text-indigo-700 font-bold uppercase tracking-wider ml-auto">
                            Extraído literal del pliego
                        </span>
                    </div>
                    <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
                        <table className="w-full data-table text-sm">
                            <thead className="sticky top-0 bg-indigo-50/95 backdrop-blur z-10">
                                <tr>
                                    <th className="w-44">Capítulo</th>
                                    <th className="w-24">Código</th>
                                    <th>Descripción</th>
                                    <th className="w-16">Ud.</th>
                                    <th className="w-20">Cant.</th>
                                    <th className="w-28">P. unitario</th>
                                    <th className="w-32">Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis.bill_of_quantities.map((it, i) => (
                                    <tr key={`boq-${i}`} data-testid={`boq-row-${i}`}>
                                        <td className="text-xs text-indigo-700 font-medium">
                                            {it.chapter || "—"}
                                        </td>
                                        <td className="font-mono text-xs text-slate-500">
                                            {it.code || "—"}
                                        </td>
                                        <td className="text-slate-700 leading-snug">
                                            {it.description || "—"}
                                        </td>
                                        <td className="text-xs text-slate-500">
                                            {it.unit || "—"}
                                        </td>
                                        <td className="num text-xs">
                                            {it.quantity || "—"}
                                        </td>
                                        <td className="num font-mono text-xs text-slate-700">
                                            {it.unit_price || "—"}
                                        </td>
                                        <td className="num font-mono text-xs text-emerald-700 font-bold">
                                            {it.amount || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Extra critical fields row (convenio · lugar · solvencia económica · solvencia técnica) */}
            {(analysis.collective_agreement ||
                analysis.work_location ||
                analysis.economic_solvency ||
                analysis.technical_solvency) && (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200 bg-white">
                    {analysis.collective_agreement && (
                        <div className="p-5 md:border-r border-slate-200">
                            <div className="flex items-center gap-2 overline mb-3">
                                <Users size={12} /> Convenio colectivo
                            </div>
                            <div className="text-sm text-slate-700 font-medium leading-relaxed">
                                {analysis.collective_agreement}
                            </div>
                        </div>
                    )}
                    {analysis.work_location && (
                        <div className="p-5 md:border-r border-slate-200 border-t md:border-t-0">
                            <div className="flex items-center gap-2 overline mb-3">
                                <MapPin size={12} /> Lugar de prestación
                            </div>
                            <div className="text-sm text-slate-700 font-medium leading-relaxed">
                                {analysis.work_location}
                            </div>
                        </div>
                    )}
                    {analysis.economic_solvency && (
                        <div className="p-5 lg:border-r border-slate-200 border-t lg:border-t-0">
                            <div className="flex items-center gap-2 overline mb-3">
                                <Banknote size={12} /> Solvencia económica
                            </div>
                            <div className="text-sm text-slate-700 font-medium leading-relaxed">
                                {analysis.economic_solvency}
                            </div>
                        </div>
                    )}
                    {analysis.technical_solvency && (
                        <div className="p-5 border-t lg:border-t-0">
                            <div className="flex items-center gap-2 overline mb-3">
                                <Users size={12} /> Solvencia técnica
                            </div>
                            <div className="text-sm text-slate-700 font-medium leading-relaxed">
                                {analysis.technical_solvency}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Grid of details */}
            <div className="grid md:grid-cols-2 gap-5">
                <Card title="Plazos clave" icon={Calendar}>
                    {analysis.key_deadlines?.length ? (
                        <ul className="space-y-2.5">
                            {analysis.key_deadlines.map((d, i) => (
                                <li
                                    key={`${i}-${d.label}`}
                                    className="flex items-start justify-between gap-3 pb-2.5 border-b border-slate-100 last:border-0"
                                >
                                    <span className="text-sm text-slate-700 leading-relaxed">
                                        {d.label}
                                    </span>
                                    <span className="font-mono text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-xl shrink-0">
                                        {d.date}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-sm text-slate-400">Sin plazos identificados</div>
                    )}
                </Card>

                <Card title="Requisitos técnicos" icon={ScrollText}>
                    <List items={analysis.technical_requirements} />
                </Card>

                <Card title="Solvencia exigida" icon={Scale}>
                    <List items={analysis.solvency_requirements} />
                </Card>

                <Card title="Criterios de valoración" icon={Target}>
                    {analysis.evaluation_criteria?.length ? (
                        <ul className="space-y-2.5">
                            {analysis.evaluation_criteria.map((c, i) => (
                                <li
                                    key={`${i}-${c.criterion}`}
                                    className="flex items-center justify-between gap-3 pb-2 border-b border-slate-100 last:border-0"
                                >
                                    <span className="text-sm text-slate-700">{c.criterion}</span>
                                    <span className="font-mono text-xs bg-[#0055ff] text-white px-2 py-0.5 rounded-xl shrink-0">
                                        {c.weight}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-sm text-slate-400">Sin criterios extraídos</div>
                    )}
                </Card>

                <Card title="Anexos requeridos" icon={Building2}>
                    <List items={analysis.required_annexes} />
                </Card>

                <Card title="Riesgos" icon={AlertTriangle}>
                    {analysis.risks?.length ? (
                        <ul className="space-y-2">
                            {analysis.risks.map((r, i) => (
                                <li
                                    key={`risk-${i}-${r.slice(0, 30)}`}
                                    className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed"
                                >
                                    <AlertTriangle
                                        size={13}
                                        className="text-amber-600 mt-1 shrink-0"
                                    />
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-sm text-slate-400">Sin riesgos identificados</div>
                    )}
                </Card>

                {analysis.opportunities?.length > 0 && (
                    <Card title="Oportunidades" icon={Sparkles} span="md:col-span-2">
                        <List items={analysis.opportunities} />
                    </Card>
                )}

                {analysis.improvements?.length > 0 && (
                    <Card title="Mejoras valorables" icon={BadgePercent}>
                        <List items={analysis.improvements} />
                    </Card>
                )}

                {analysis.penalties?.length > 0 && (
                    <Card title="Penalidades" icon={AlertTriangle}>
                        <ul className="space-y-2">
                            {analysis.penalties.map((p, i) => (
                                <li
                                    key={`pen-${i}-${p.slice(0, 30)}`}
                                    className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed"
                                >
                                    <AlertTriangle
                                        size={13}
                                        className="text-red-600 mt-1 shrink-0"
                                    />
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                )}
            </div>
        </div>
    );
};

// ---------- Operational details (per contract-type) ----------

const TYPE_META = {
    seguridad: { icon: Shield, label: "Servicio de seguridad", color: "rose" },
    limpieza: { icon: Brush, label: "Servicio de limpieza", color: "cyan" },
    mantenimiento: { icon: Wrench, label: "Mantenimiento integral", color: "amber" },
    obras: { icon: HardHat, label: "Ejecución de obra", color: "orange" },
    suministros: { icon: Package, label: "Suministros", color: "violet" },
    servicios_profesionales: {
        icon: Briefcase,
        label: "Servicios profesionales",
        color: "indigo",
    },
    transporte: { icon: Truck, label: "Servicios de transporte", color: "sky" },
    restauracion: { icon: Coins, label: "Restauración / catering", color: "amber" },
    sanitario: { icon: ShieldCheck, label: "Servicios sanitarios", color: "emerald" },
    tecnologia: { icon: Sparkles, label: "Servicios tecnológicos", color: "blue" },
    otros: { icon: Layers, label: "Detalle operativo", color: "slate" },
};

// Pre-known sub-block configs so the order is always the same.
const SUB_BLOCKS = [
    "security",
    "works",
    "cleaning",
    "maintenance",
    "supplies",
    "professional_services",
    "transport",
];

function OperationalDetailsBlock({ details }) {
    if (!details || typeof details !== "object") return null;
    const filled = SUB_BLOCKS.filter((k) => {
        const v = details[k];
        return v && typeof v === "object" && Object.keys(v).length > 0;
    });
    if (filled.length === 0) return null;

    const ctype = (details.contract_type || "otros").toLowerCase();
    const meta = TYPE_META[ctype] || TYPE_META.otros;
    const Icon = meta.icon;

    return (
        <div
            className="bg-white border border-brand-200 rounded-xl overflow-hidden"
            data-testid="operational-details-block"
        >
            <div className="px-5 py-3 border-b border-brand-200 bg-brand-50/60 flex items-center gap-2">
                <Icon size={14} className="text-brand-700" />
                <div className="font-display font-bold text-ink-950">
                    Detalle operativo — {meta.label}
                </div>
                <span className="text-xs text-brand-700 font-bold uppercase tracking-wider ml-auto">
                    ⚙️ Datos extraídos literal del pliego
                </span>
            </div>
            <div className="p-5 space-y-5">
                {filled.map((key) => (
                    <SubBlock key={key} kind={key} data={details[key]} />
                ))}
            </div>
        </div>
    );
}

function SubBlock({ kind, data }) {
    if (kind === "security") return <SecurityBlock data={data} />;
    if (kind === "works") return <WorksBlock data={data} />;
    if (kind === "cleaning") return <CleaningBlock data={data} />;
    if (kind === "maintenance") return <MaintenanceBlock data={data} />;
    if (kind === "supplies") return <SuppliesBlock data={data} />;
    if (kind === "professional_services")
        return <ProfessionalServicesBlock data={data} />;
    if (kind === "transport") return <TransportBlock data={data} />;
    return null;
}

// Helpers ---------------------------------------------------------------------

function KeyVal({ label, value }) {
    if (value === undefined || value === null || value === "" || value === 0) return null;
    return (
        <div className="bg-slate-50 rounded-lg px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {label}
            </div>
            <div className="text-sm text-ink-950 font-medium mt-0.5 break-words">
                {String(value)}
            </div>
        </div>
    );
}

function Chips({ label, items }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                {label}
            </div>
            <div className="flex flex-wrap gap-1.5">
                {items.map((it, i) => (
                    <span
                        key={i}
                        className="bg-brand-50 text-brand-700 border border-brand-100 px-2.5 py-1 rounded-md text-xs font-medium"
                    >
                        {String(it)}
                    </span>
                ))}
            </div>
        </div>
    );
}

function SubHeading({ children }) {
    return (
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-500 mb-2">
            {children}
        </div>
    );
}

// Block components ------------------------------------------------------------

function SecurityBlock({ data }) {
    const posts = Array.isArray(data.puestos_servicio) ? data.puestos_servicio : [];
    return (
        <div className="space-y-4" data-testid="op-security">
            {posts.length > 0 && (
                <div>
                    <SubHeading>Puestos de servicio</SubHeading>
                    <div className="overflow-x-auto">
                        <table className="w-full data-table text-sm">
                            <thead>
                                <tr>
                                    <th>Ubicación</th>
                                    <th>Tipo</th>
                                    <th className="text-center">Nº</th>
                                    <th>Días</th>
                                    <th>Horario</th>
                                    <th className="text-right">Horas/año</th>
                                </tr>
                            </thead>
                            <tbody>
                                {posts.map((p, i) => (
                                    <tr key={i}>
                                        <td className="font-medium">{p.ubicacion || "—"}</td>
                                        <td>{p.tipo || "—"}</td>
                                        <td className="text-center font-mono font-bold">
                                            {p.n_vigilantes ?? "—"}
                                        </td>
                                        <td>{p.dias || "—"}</td>
                                        <td className="font-mono text-xs">
                                            {p.horario || "—"}
                                        </td>
                                        <td className="text-right font-mono font-bold text-brand-700">
                                            {p.horas_servicio
                                                ? p.horas_servicio.toLocaleString("es-ES")
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                <KeyVal label="Total horas anuales" value={data.total_horas_anuales} />
                <KeyVal
                    label="Vigilantes mínimos en plantilla"
                    value={data.n_vigilantes_total_estimado}
                />
                <KeyVal label="Arma" value={data.arma} />
                <KeyVal label="Uniforme" value={data.uniforme} />
                <KeyVal label="Centro de control" value={data.centro_control} />
                <KeyVal label="Formación obligatoria" value={data.formacion_obligatoria} />
            </div>
            <Chips label="Medios materiales" items={data.medios_materiales} />
        </div>
    );
}

function WorksBlock({ data }) {
    return (
        <div className="space-y-3" data-testid="op-works">
            <SubHeading>Alcance de la obra</SubHeading>
            <div className="grid sm:grid-cols-2 gap-2">
                <KeyVal label="Ubicación" value={data.ubicacion_obra} />
                <KeyVal label="Tipo de actuación" value={data.tipo_actuacion} />
                <KeyVal label="Superficie" value={data.superficie_actuacion} />
                <KeyVal label="Plazo de ejecución" value={data.plazo_ejecucion} />
            </div>
            {data.alcance_principal && (
                <div className="bg-amber-50/40 border border-amber-200 rounded-lg p-3 text-sm leading-relaxed">
                    {data.alcance_principal}
                </div>
            )}
            <Chips label="Fases de obra" items={data.fases} />
            <Chips label="Materiales clave" items={data.materiales_clave} />
            <KeyVal label="Maquinaria principal" value={data.maquinaria_principal} />
            <Chips label="Documentación obligatoria" items={data.documentacion_obra} />
        </div>
    );
}

function CleaningBlock({ data }) {
    const centers = Array.isArray(data.centros) ? data.centros : [];
    return (
        <div className="space-y-3" data-testid="op-cleaning">
            {centers.length > 0 && (
                <div>
                    <SubHeading>Centros de trabajo</SubHeading>
                    <div className="overflow-x-auto">
                        <table className="w-full data-table text-sm">
                            <thead>
                                <tr>
                                    <th>Centro</th>
                                    <th>Dirección</th>
                                    <th className="text-right">m²</th>
                                    <th>Tipo</th>
                                    <th>Frecuencia</th>
                                    <th>Horario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {centers.map((c, i) => (
                                    <tr key={i}>
                                        <td className="font-medium">{c.nombre || "—"}</td>
                                        <td className="text-slate-600 text-xs">
                                            {c.direccion || "—"}
                                        </td>
                                        <td className="text-right font-mono font-bold">
                                            {c.superficie_m2
                                                ? c.superficie_m2.toLocaleString("es-ES")
                                                : "—"}
                                        </td>
                                        <td className="text-xs">{c.tipo_local || "—"}</td>
                                        <td className="text-xs">{c.frecuencia || "—"}</td>
                                        <td className="font-mono text-xs">
                                            {c.horario || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
                <KeyVal label="Superficie total m²" value={data.superficie_total_m2} />
                <KeyVal label="Horas anuales" value={data.horas_servicio_anuales} />
                <KeyVal
                    label="Limpiezas extraordinarias"
                    value={data.limpiezas_extraordinarias}
                />
            </div>
            <Chips label="Tareas principales" items={data.tareas_principales} />
            <Chips label="Productos" items={data.productos} />
            <Chips label="Maquinaria" items={data.maquinaria} />
        </div>
    );
}

function MaintenanceBlock({ data }) {
    const resp = data.tiempos_respuesta || {};
    return (
        <div className="space-y-3" data-testid="op-maintenance">
            <SubHeading>Alcance del mantenimiento</SubHeading>
            <Chips label="Instalaciones objeto" items={data.instalaciones_objeto} />
            <Chips label="Centros" items={data.centros} />
            <div className="grid sm:grid-cols-2 gap-2">
                <KeyVal label="Tipo de mantenimiento" value={data.tipo_mantenimiento} />
                <KeyVal label="Frecuencias legales" value={data.frecuencias_legales} />
                <KeyVal label="Guardia 24h" value={data.guardia_24h} />
                <KeyVal
                    label="Repuestos / consumibles"
                    value={data.repuestos_consumibles_incluidos}
                />
            </div>
            {Object.keys(resp).length > 0 && (
                <div>
                    <SubHeading>Tiempos de respuesta</SubHeading>
                    <div className="grid sm:grid-cols-3 gap-2">
                        {Object.entries(resp).map(([k, v]) => (
                            <KeyVal key={k} label={k} value={v} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function SuppliesBlock({ data }) {
    const items = Array.isArray(data.articulos_lote) ? data.articulos_lote : [];
    return (
        <div className="space-y-3" data-testid="op-supplies">
            {items.length > 0 && (
                <div>
                    <SubHeading>Artículos del lote</SubHeading>
                    <div className="overflow-x-auto">
                        <table className="w-full data-table text-sm">
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Descripción</th>
                                    <th>Unidad</th>
                                    <th className="text-right">Cantidad</th>
                                    <th>Marca / referencia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, i) => (
                                    <tr key={i}>
                                        <td className="font-mono text-xs">{it.codigo || "—"}</td>
                                        <td className="font-medium">{it.descripcion || "—"}</td>
                                        <td className="text-xs">{it.unidad || "—"}</td>
                                        <td className="text-right font-mono font-bold">
                                            {it.cantidad_estimada
                                                ? Number(
                                                      it.cantidad_estimada,
                                                  ).toLocaleString("es-ES")
                                                : "—"}
                                        </td>
                                        <td className="text-xs text-slate-600">
                                            {it.marca_referencia || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                <KeyVal label="Entrega estándar" value={data.plazo_entrega_estandar} />
                <KeyVal label="Entrega urgente" value={data.plazo_entrega_urgente} />
                <KeyVal label="Lugar de entrega" value={data.lugar_entrega} />
                <KeyVal label="Horario" value={data.horario_entregas} />
                <KeyVal label="Garantía" value={data.garantia_articulos} />
                <KeyVal label="Mínimo de pedido" value={data.minimos_pedido} />
            </div>
        </div>
    );
}

function ProfessionalServicesBlock({ data }) {
    const profiles = Array.isArray(data.perfiles_requeridos)
        ? data.perfiles_requeridos
        : [];
    return (
        <div className="space-y-3" data-testid="op-professional">
            {profiles.length > 0 && (
                <div>
                    <SubHeading>Perfiles requeridos</SubHeading>
                    <div className="overflow-x-auto">
                        <table className="w-full data-table text-sm">
                            <thead>
                                <tr>
                                    <th>Perfil</th>
                                    <th className="text-right">Jornadas</th>
                                    <th>Experiencia mín.</th>
                                    <th>Titulación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {profiles.map((p, i) => (
                                    <tr key={i}>
                                        <td className="font-medium">{p.perfil || "—"}</td>
                                        <td className="text-right font-mono font-bold">
                                            {p.n_jornadas || "—"}
                                        </td>
                                        <td className="text-xs">
                                            {p.experiencia_min || "—"}
                                        </td>
                                        <td className="text-xs text-slate-600">
                                            {p.titulacion || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
                <KeyVal label="Presencia en oficina" value={data.presencia_oficina} />
                <KeyVal
                    label="Metodología obligatoria"
                    value={data.metodologia_obligatoria}
                />
            </div>
            <Chips label="Entregables" items={data.entregables} />
        </div>
    );
}

function TransportBlock({ data }) {
    const rutas = Array.isArray(data.rutas) ? data.rutas : [];
    return (
        <div className="space-y-3" data-testid="op-transport">
            {rutas.length > 0 && (
                <div>
                    <SubHeading>Rutas</SubHeading>
                    <div className="overflow-x-auto">
                        <table className="w-full data-table text-sm">
                            <thead>
                                <tr>
                                    <th>Origen</th>
                                    <th>Destino</th>
                                    <th>Frecuencia</th>
                                    <th className="text-right">Km/ruta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rutas.map((r, i) => (
                                    <tr key={i}>
                                        <td className="font-medium">{r.origen || "—"}</td>
                                        <td>{r.destino || "—"}</td>
                                        <td className="text-xs">{r.frecuencia || "—"}</td>
                                        <td className="text-right font-mono font-bold">
                                            {r.km_estimados
                                                ? Number(r.km_estimados).toLocaleString("es-ES")
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
                <KeyVal label="Tipo de vehículo" value={data.tipo_vehiculo} />
                <KeyVal label="Mercancía" value={data.mercancia} />
                <KeyVal label="Tonelaje anual" value={data.tonelaje_anual_estimado} />
                <KeyVal label="Horarios de entrega" value={data.horarios_entrega} />
            </div>
        </div>
    );
}

export default AnalysisView;
